import { theme } from "@theme";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { TextInput } from "react-native";
import LBox from "../LBox";
import LIcon from "../LIcon";
import LInput from "../LInput";

type SearchBoxProps = {
  showIcon?: boolean;
  placeholder?: string;
  onChange: (newValue: string) => void;
  onBlur?: () => void;
  onClear?: () => void;
  onFocus?: () => void;
  onSubmitEditing?: () => void;
  value: string;
  editable?: boolean;
  focusOnMount?: boolean;
};

export type SearchTextBoxRef = {
  focus: () => void;
};

export const SearchTextBox = forwardRef<SearchTextBoxRef, SearchBoxProps>(
  function SearchTextBoxComp(
    {
      value,
      onChange,
      editable = true,
      focusOnMount = false,
      ...rest
    }: SearchBoxProps,
    forwardedRef,
  ) {
    const ref = useRef<TextInput>(null);

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus: () => ref.current?.focus(),
      }),
      [],
    );

    useEffect(() => {
      if (focusOnMount) {
        // on Android without delay of 1ms, the input is focused but the keyboard is not shown
        // other workaround is using onLayout to focus the input
        setTimeout(() => {
          ref.current?.focus();
        }, 1);
      }
    }, []);

    return (
      <LBox
        justifyContent="center"
        width="100%"
        pointerEvents={editable ? undefined : "none"}
      >
        <LInput
          ref={ref}
          value={value}
          onChangeText={onChange}
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="off"
          variant={"searchBar"}
          placeholderTextColor={theme.colors.searchInputPlaceholderText}
          placeholder={rest.placeholder}
          onBlur={rest.onBlur}
          onFocus={rest.onFocus}
          onSubmitEditing={rest.onSubmitEditing}
          editable={editable}
          returnKeyType="search"
          style={{
            paddingHorizontal: theme.spacing.m1,
          }}
        />
        {(rest.showIcon || value.length > 0) && (
          <LIcon
            colors={
              value?.length > 0 ? undefined : { color: "#E6765A" }
            }
            sentryEventName="search_clear_text_button"
            name={value?.length > 0 ? "circle_cross" : "search"}
            strokeWidth={2}
            size={value.length > 0 ? 20 : 28}
            onPress={() => {
              if (value?.length > 0) {
                onChange("");
                rest.onClear?.();
              }
            }}
            position="absolute"
            right={theme.spacing.s3}
          />
        )}
      </LBox>
    );
  },
);
